<template>
  <div>
    <HeroSection
      :nav-breadcrumbs="[
        {
          href: '/training',
          title: 'Training'
        },
        {
          href: '/training/courses',
          title: 'Courses'
        },
        {
          href: canonicalPaths.relative,
          title: course.abbreviated_name || course.name
        }
      ]"
      :value="heroSection"
    >
      <template v-if="coursePage.hero_section.cta" #cta>
        <HeroCta :value="coursePage.hero_section.cta" />
      </template>
    </HeroSection>

    <template v-if="course?.sell_bullets">
      <SellOccasionsSection
        :scroll-to-section="scrollToSection"
        :value="course"
        class="mb-16"
      />
      <CourseAboutSection
        :course="course"
        :value="introSection"
        class="mb-16"
      />
    </template>
    <CourseIntroSection
      v-else
      :course="course"
      :scroll-to-section="scrollToSection"
      :value="introSection"
      class="mb-16 hero-section hero-below-offset"
    />

    <MidPageNavBar
      :nav-items="[
        {
          key: 'overview',
          title: 'Overview'
        },
        {
          key: 'trainers',
          title: 'Trainers'
        },
        {
          key: 'reviews',
          hidden: !reviews.length,
          title: 'Reviews'
        },
        {
          key: 'register',
          title: 'Find a Class'
        },
        {
          key: 'resources',
          title: 'Resources'
        }
      ]"
      class="mb-1"
      @click="scrollToSection"
    />

    <CourseOverviewSection
      :ref="
        el => {
          sectionRefs.overview = el
        }
      "
      :course="course"
      :value="coursePage.course_overview_section"
      class="mb-16"
    />

    <CourseDetailSection
      v-if="course.detail_section"
      :course="course"
      :scroll-to-section="scrollToSection"
      :value="coursePage.course_detail_section"
      class="mb-16"
    />

    <DividerSection v-if="course.detail_section" class="mb-16" />

    <TrainerPersonsSection
      :ref="
        el => {
          sectionRefs.trainers = el
        }
      "
      :filters-value="{ productCodes }"
      :value="coursePage.trainers_section"
      class="mb-16"
      hide-filters
      upcoming
    />

    <ReviewsSection
      :ref="
        el => {
          sectionRefs.reviews = el
        }
      "
      :items="reviews"
      class="mb-16"
    />

    <v-container
      :ref="
        el => {
          sectionRefs.register = el
        }
      "
    />

    <v-lazy>
      <OccasionsSection
        :banner="banner"
        :filters="{ what: false, who: false }"
        :filters-value="{ productCodes }"
        :value="coursePage.classes_section"
        class="mb-16"
        hide-info-button
      />
    </v-lazy>

    <IconDifferentiatorsSection
      class="mb-16"
      :value="iconDifferentiatorsSection"
    />

    <PromotedResourcesSection
      :ref="
        el => {
          sectionRefs.resources = el
        }
      "
      :value="coursePage.promoted_resources_section"
      class="mb-16"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from 'vue'
import { useDisplay } from 'vuetify'
import { NAV_BAR_HEIGHT } from '#root/lib/consts'
import { scrollToRefKey } from '#root/lib/utils'
import { usePageContext } from '#root/renderer/usePageContext'
import { useProvideDeliveryMethodsStore } from '#root/lib/useDeliveryMethodsStore'
import { useProvideTrainerPersonsStore } from '#root/lib/useTrainerPersonsStore'

const { lgAndUp } = useDisplay()
const sectionRefs = reactive({})

const props = defineProps({
  banner: {
    default: undefined,
    type: Object
  },
  course: {
    required: true,
    type: Object
  },
  coursePage: {
    required: true,
    type: Object
  },
  deliveryMethods: {
    required: true,
    type: Array
  },
  productCodes: {
    required: true,
    type: Array
  },
  reviews: {
    required: true,
    type: Array
  },
  trainers: {
    required: true,
    type: Array
  }
})

useProvideDeliveryMethodsStore(props.deliveryMethods)
useProvideTrainerPersonsStore(props.trainers)

const { canonicalPaths } = usePageContext()
const heroSection = computed(() => {
  const section = Object.assign({}, props.coursePage.hero_section)
  section.title = props.course.abbreviated_name
    ? `${props.course.name} (${props.course.abbreviated_name})`
    : props.course.name
  return section
})
const iconDifferentiatorsSection = computed(() => {
  return Object.assign(
    {},
    props.coursePage.icon_differentiators_section,
    props.course.icon_differentiators_section
  )
})
const introSection = computed(() => {
  return Object.assign(
    {},
    props.coursePage.course_intro_section,
    props.course.intro_section
  )
})

function scrollToSection({ key }) {
  scrollToRefKey(sectionRefs, key, {
    height: lgAndUp.value ? NAV_BAR_HEIGHT : 0
  })
}

onMounted(async () => {
  await scrollToRefKey(sectionRefs, undefined, {
    delay: true,
    height: lgAndUp.value ? NAV_BAR_HEIGHT : 0
  })
})
</script>
